import { useEffect, useState } from "react";
import { useRouteError, Link } from "react-router-dom";
import ErrorSvg from "@/assets/illustrations/error.svg";
import { Button } from "@/components/ui/button";
import { useUser } from "@/hooks/useUser";
import { RefreshCcw, Home, CheckCircle2 } from "lucide-react"; // Asumsi menggunakan lucide-react untuk icon

interface RouteError {
  status?: number;
  statusText?: string;
  message?: string;
}

export default function ErrorPage() {
  const { user } = useUser();
  const error = useRouteError() as RouteError;
  const [isReloading, setIsReloading] = useState(false);

  // Sebaiknya gunakan environment variable: import.meta.env.VITE_DISCORD_WEBHOOK_URL
  const discordWebhookUrl =
    "https://discord.com/api/webhooks/1480389427586138263/UzU6aw9xQZcJ_QxOsTCPbz8gXLqw3nbbgOB2Wb3RhVF_8zju6Yel--aw8HNjrNxkLhy8";

  const { location } = window;
  const isHttps = location.origin.startsWith("https");

  const refreshPage = () => {
    setIsReloading(true);
    // Beri sedikit delay agar user melihat animasi loading sebelum reload beneran
    setTimeout(() => {
      window.location.reload();
    }, 600);
  };

  useEffect(() => {
    if (isHttps && error && discordWebhookUrl) {
      const payload = {
        content: `🚨 **Error Page Triggered!**\n**URL:** ${window.location.href}\n**User Name:** ${user ? user?.nama_lengkap : "Unknown/Guest"}\n**User Agent:** ${navigator.userAgent}\n**Error:** ${error.statusText || error.message || JSON.stringify(error)}`,
      };

      fetch(discordWebhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch((err) => console.error("Failed to notify Discord:", err));
    }
  }, [error, user, isHttps, discordWebhookUrl]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4 font-sans">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] p-8 md:p-10 text-center relative overflow-hidden border border-slate-100">
        {/* Aksen Background Halus di belakang */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-gradient-to-b from-blue-50 to-transparent -z-10 opacity-60"></div>

        {/* Ilustrasi Utama */}
        <img
          src={ErrorSvg}
          alt="Ilustrasi Kendala"
          className="w-48 h-48 mx-auto mb-6 object-contain drop-shadow-sm animate-in fade-in zoom-in duration-500"
        />

        {/* Badge Laporan Otomatis (UX untuk menenangkan user) */}
        <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 mb-5 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Laporan otomatis telah terkirim ke tim teknis
        </div>

        {/* Copywriting Friendly */}
        <h1 className="mb-3 font-bold text-2xl md:text-3xl text-slate-800 tracking-tight">
          Halaman Sedang Mengambil Napas
        </h1>
        <p className="text-slate-500 mb-8 leading-relaxed text-sm md:text-base px-2">
          Terjadi sedikit kendala komunikasi pada sistem. Jangan khawatir, tim
          kami sudah menerima notifikasinya. Silakan muat ulang halaman untuk
          melanjutkan aktivitas Anda.
        </p>

        {/* Aksi */}
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <Button
            onClick={refreshPage}
            disabled={isReloading}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-11 px-6 shadow-sm transition-all flex-1"
          >
            <RefreshCcw
              className={`w-4 h-4 mr-2 ${isReloading ? "animate-spin" : ""}`}
            />
            {isReloading ? "Memuat ulang..." : "Muat Ulang Halaman"}
          </Button>

          <Link to="/" className="flex-1">
            <Button
              variant="outline"
              className="w-full rounded-xl h-11 border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
            >
              <Home className="w-4 h-4 mr-2" />
              Ke Beranda
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
